/**
 * P9-P0/P9-P2B — Direction (LTR/RTL) + admin English/LTR enforcement.
 *
 * Reactively reflects the active i18n language onto `<html lang dir>` and
 * exposes `{ dir, isRtl }` via context (used by the Icon resolver for glyph
 * mirroring).
 *
 * P9-P2B decisions:
 *  • ADMIN is ALWAYS English + LTR. On any `/admin/*` route we force `lang=en`,
 *    `dir=ltr` and pin i18n to English regardless of the user's chosen language,
 *    so the admin console never flips during the multilingual rollout.
 *  • Arabic stays gated from real users (clamped to `active_languages`); a
 *    dev/QA probe `?lang=ar` (handled in languageManager) previews Arabic while
 *    gated. `?rtl=1` independently forces RTL for layout QA.
 *
 * BEHAVIOUR-NEUTRAL while Arabic is inactive: active language is `en` → `ltr`.
 * Lives INSIDE the Router so it can be route-aware via `useLocation`.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from './index';
import {
  readLanguageProbe,
  applyLanguageUnclamped,
  applyUserLanguagePreference,
} from './languageManager';

const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

// Default 'ltr' so any consumer rendered outside the provider stays neutral.
const DirectionContext = createContext('ltr');

function readRtlProbe() {
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('rtl');
    if (q === '1') {
      window.localStorage.setItem('snappipit_rtl_probe', '1');
      return true;
    }
    if (q === '0') {
      window.localStorage.removeItem('snappipit_rtl_probe');
      return false;
    }
    return window.localStorage.getItem('snappipit_rtl_probe') === '1';
  } catch {
    return false;
  }
}

function isRtlLang(lang) {
  return RTL_LANGUAGES.has(String(lang || '').split('-')[0]);
}

export const DirectionProvider = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  const [lang, setLang] = useState(i18n.language || 'en');
  const [dir, setDir] = useState('ltr');

  // Attach the languageChanged listener FIRST so subsequent changes update state.
  useEffect(() => {
    const onLangChanged = (lng) => setLang(lng);
    i18n.on('languageChanged', onLangChanged);
    return () => i18n.off('languageChanged', onLangChanged);
  }, []);

  // Apply the QA language probe on mount (e.g. ?lang=ar previews Arabic) and
  // immediately sync local state so the direction effect re-runs.
  useEffect(() => {
    const probe = readLanguageProbe();
    if (probe) {
      applyLanguageUnclamped(probe);
      setLang(probe);
    }
  }, []);

  // Admin English/LTR enforcement: pin i18n to English on admin routes; restore
  // the user's resolved language (incl. probe) when leaving admin.
  useEffect(() => {
    if (isAdmin) {
      if (i18n.language !== 'en') i18n.changeLanguage('en');
    } else {
      const probe = readLanguageProbe();
      if (probe) applyLanguageUnclamped(probe);
      else applyUserLanguagePreference(undefined);
    }
  }, [isAdmin]);

  useEffect(() => {
    const effectiveLang = isAdmin ? 'en' : lang;
    const nextDir = isAdmin ? 'ltr' : (readRtlProbe() || isRtlLang(effectiveLang) ? 'rtl' : 'ltr');
    setDir(nextDir);
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.setAttribute('lang', String(effectiveLang || 'en').split('-')[0]);
      root.setAttribute('dir', nextDir);
    }
  }, [lang, isAdmin]);

  return <DirectionContext.Provider value={dir}>{children}</DirectionContext.Provider>;
};

export function useDirection() {
  const dir = useContext(DirectionContext);
  return { dir, isRtl: dir === 'rtl' };
}

export default DirectionContext;
